import React from 'react'
import Header from './header'
import Footer from './Footer'
import CustomerHeader from '../customer/CustomerHeader'
import Newsletter from './HomeNewsletter'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { Link } from 'react-router-dom'
import { Target, Compass, Award, Newspaper, BarChart3, ShieldAlert } from 'lucide-react'

function About() {
  const { isAuthenticated } = useCustomerAuth();
  
  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Conditionally render header based on authentication */}
      {isAuthenticated ? <CustomerHeader /> : <Header />}

      {/* Hero Banner Section */}
      <section className="premium-about-hero">
        <span className="premium-about-hero-badge">About Us</span>
        <h1 className="premium-about-hero-title">SkillSeek connects people with<br/> great local businesses.</h1>
        <p className="premium-about-hero-subtitle">
          Providing a reliable, transparent, and trustworthy space where clients and professionals 
          connect with clarity and confidence.
        </p>
      </section>

      {/* Main Narrative Intro Section */}
      <section className="premium-about-grid">
        <div className="premium-about-left">
          <h2>Redefining how local services are exchanged.</h2>
          <p>
            SkillSeek was created to solve a simple but widespread problem: finding skilled workers 
            who deliver quality and professionalism. Many people, ourselves included, have faced 
            the frustration of searching for the right person, only to be left uncertain or disappointed.
          </p>
          <p>
            We provide a reliable digital marketplace where clients and workers can connect with absolute 
            clarity. It’s designed to make the process of finding professional help — or offering 
            your skills — consistent, transparent, and trustworthy.
          </p>
        </div>

        <div className="premium-about-right">
          {/* Mission Card */}
          <div className="premium-about-card">
            <div className="premium-about-card-title">
              <Target size={24} />
              <span>Our Mission</span>
            </div>
            <p className="premium-about-card-desc">
              To transform the way people connect with skills — creating an environment where 
              professionalism thrives, clients find trusted help with ease, and workers are 
              empowered to grow and succeed.
            </p>
          </div>

          {/* Vision Card */}
          <div className="premium-about-card">
            <div className="premium-about-card-title">
              <Compass size={24} />
              <span>Our Vision</span>
            </div>
            <p className="premium-about-card-desc">
              To become the first choice for anyone looking to hire or offer services. We see a future 
              where SkillSeek makes connection simple and reliable, raising the standard for service 
              exchange across communities.
            </p>
          </div>
        </div>
      </section>

      {/* How SkillSeek Works Section */}
      <section className="premium-about-how">
        <h2 className="premium-about-how-title">How SkillSeek Works</h2>
        <p className="premium-about-how-subtitle">Connecting Jamaica's best local professionals with people who need work done.</p>
        
        <div className="premium-about-how-grid">
          {/* For Customers */}
          <div className="premium-about-how-column">
            <h3>For Customers</h3>
            <div className="premium-about-how-steps">
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">1</div>
                <div className="premium-about-how-step-content">
                  <h4>Search & Filter</h4>
                  <p>Browse listings by category, search for specific services or skills, and filter providers by parish location or rating.</p>
                </div>
              </div>
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">2</div>
                <div className="premium-about-how-step-content">
                  <h4>View & Compare Profiles</h4>
                  <p>Check ratings, view detailed descriptions, portfolio works, hourly rates, and read authentic feedback from previous clients.</p>
                </div>
              </div>
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">3</div>
                <div className="premium-about-how-step-content">
                  <h4>Connect & Book</h4>
                  <p>Directly contact professionals, describe your project requirements, get quotes, and hire with total transparency.</p>
                </div>
              </div>
            </div>
          </div>

          {/* For Service Providers */}
          <div className="premium-about-how-column">
            <h3>For Skilled Workers</h3>
            <div className="premium-about-how-steps">
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">1</div>
                <div className="premium-about-how-step-content">
                  <h4>List Your Services</h4>
                  <p>Register as a skilled worker, create a details-rich profile, mention your skills, hourly rates, and showcase your best works.</p>
                </div>
              </div>
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">2</div>
                <div className="premium-about-how-step-content">
                  <h4>Get Discovered</h4>
                  <p>Show up on public search grids and interactive maps for clients looking for your expertise in your specific parish.</p>
                </div>
              </div>
              <div className="premium-about-how-step">
                <div className="premium-about-how-step-num">3</div>
                <div className="premium-about-how-step-content">
                  <h4>Grow Your Business</h4>
                  <p>Receive direct job inquiries from clients, build lasting professional relationships, and earn 5-star ratings for quality work.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values / Support Columns Section */}
      <section className="premium-about-values-sec">
        <h2 className="premium-about-values-title">More about SkillSeek</h2>
        <p className="premium-about-values-subtitle">Discover opportunities, insights, and our commitment to trust and community.</p>
        
        <div className="premium-about-values-grid">
          {/* Careers */}
          <div className="premium-value-card">
            <div className="premium-value-icon-wrapper">
              <Award size={24} />
            </div>
            <h4>Careers</h4>
            <p>Start a five-star career with meaningful opportunities, engaging learning programs, and a rich culture.</p>
          </div>

          {/* Newsroom */}
          <div className="premium-value-card">
            <div className="premium-value-icon-wrapper">
              <Newspaper size={24} />
            </div>
            <h4>Newsroom</h4>
            <p>Find the latest news, announcements, product updates, and media resources about SkillSeek.</p>
          </div>

          {/* Investor Relations */}
          <div className="premium-value-card">
            <div className="premium-value-icon-wrapper">
              <BarChart3 size={24} />
            </div>
            <h4>Investors</h4>
            <p>Get detailed financial information, public statements, and corporate reports about SkillSeek.</p>
          </div>

          {/* Trust & Safety */}
          <div className="premium-value-card">
            <div className="premium-value-icon-wrapper">
              <ShieldAlert size={24} />
            </div>
            <h4>Trust & Safety</h4>
            <p>Learn how we work hard to keep our community safe, protect customer data, and maintain trust.</p>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  )
}

export default About
